import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

const SELLER = {
  name: 'Audio Trader Ltd',
  address: [
    'Unit 36',
    '36 Eldon Way',
    'Wick',
    'Littlehampton',
    'West Sussex',
    'BN17 7HE',
  ],
  registeredOfficeAddress: [
    'City Gates',
    '2/4 Southgate',
    'Chichester',
    'West Sussex',
    'PO19 8DJ',
  ],
  vatNumber: 'GB483154877',
  companyNumber: '14993737',
}

const VAT_RATE = 0.20

function money(n) {
  return (Number(n) || 0).toFixed(2)
}

function formatInvoiceDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d)) return ''
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function customerAddressLines(addr) {
  if (!addr) return []
  const cityLine = [addr.city, addr.province || addr.province_code, addr.zip]
    .filter(Boolean)
    .join(', ')
  return [
    addr.address1,
    addr.address2,
    cityLine,
    addr.country || addr.country_code,
  ].filter(Boolean)
}

function buildLineItems(order) {
  const lines = (order.line_items || []).map(li => {
    const unitGross = parseFloat(li.price) || 0
    const qty = Number(li.quantity) || 1
    const unitNet = unitGross / (1 + VAT_RATE)
    const lineNet = unitNet * qty
    const desc = li.variant_title ? `${li.title} — ${li.variant_title}` : (li.title || 'Item')
    return { description: desc, qty, unitNet, lineNet }
  })

  for (const ship of order.shipping_lines || []) {
    const gross = parseFloat(ship.price) || 0
    if (gross === 0) continue
    const net = gross / (1 + VAT_RATE)
    lines.push({
      description: ship.title || 'Shipping',
      qty: 1,
      unitNet: net,
      lineNet: net,
    })
  }

  const discountGross = parseFloat(order.total_discounts) || 0
  if (discountGross > 0) {
    const discountNet = discountGross / (1 + VAT_RATE)
    const label = order.discount_applications?.[0]?.title
      || order.discount_codes?.[0]?.code
      || 'Discount'
    lines.push({
      description: `Discount — ${label}`,
      qty: 1,
      unitNet: -discountNet,
      lineNet: -discountNet,
    })
  }

  if (lines.length === 0) {
    const gross = parseFloat(order.total_price) || 0
    const net = gross / (1 + VAT_RATE)
    lines.push({ description: 'Order', qty: 1, unitNet: net, lineNet: net })
  }
  return lines
}

export function generateVatInvoicePdf(order) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageW = 210
  const pageH = 297
  const marginX = 15
  const contentW = pageW - 2 * marginX

  // ---- Seller block (top-left) ----
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(13)
  doc.text(SELLER.name, marginX, 20)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  SELLER.address.forEach((line, i) => {
    doc.text(line, marginX, 26 + i * 4)
  })
  doc.text(`VAT No: ${SELLER.vatNumber}`, marginX, 26 + SELLER.address.length * 4 + 2)

  // ---- INVOICE heading (top-right) ----
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(26)
  doc.text('INVOICE', pageW - marginX, 26, { align: 'right' })

  // ---- Invoice Address (customer) ----
  const boxTop = 62
  const boxH = 30

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.text('Invoice Address', marginX, boxTop - 2)

  const customerName = [order.customer?.first_name, order.customer?.last_name]
    .filter(Boolean)
    .join(' ')
  doc.setFont('helvetica', 'bold')
  doc.text(customerName || '—', marginX, boxTop + 4)
  doc.setFont('helvetica', 'normal')
  const addrLines = customerAddressLines(order.shipping_address)
  addrLines.forEach((line, i) => {
    doc.text(line, marginX, boxTop + 9 + i * 4)
  })

  // ---- Meta strip: Invoice No | Invoice Date | Customer Ref / Order No | Account No ----
  const metaTop = boxTop + boxH + 5
  const metaH = 14
  const metaCols = 4
  const colW = contentW / metaCols
  for (let i = 0; i < metaCols; i++) {
    doc.rect(marginX + i * colW, metaTop, colW, metaH)
  }
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.text('Invoice No.', marginX + 2, metaTop + 4)
  doc.text('Invoice Date', marginX + colW + 2, metaTop + 4)
  doc.text('Customer Ref / Order No.', marginX + 2 * colW + 2, metaTop + 4)
  doc.text('Account No.', marginX + 3 * colW + 2, metaTop + 4)

  const orderNum = String(order.order_number || order.name || '')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.text(orderNum, marginX + 2, metaTop + 11)
  doc.text(formatInvoiceDate(order.created_at), marginX + colW + 2, metaTop + 11)
  doc.text(orderNum, marginX + 2 * colW + 2, metaTop + 11)
  // Account No. intentionally blank

  // ---- Items table ----
  const items = buildLineItems(order)
  const tableY = metaTop + metaH + 6

  autoTable(doc, {
    startY: tableY,
    head: [['Description of Work / Service', 'Qty / Hours', 'Rate (£)', 'Amount (£)']],
    body: items.map(it => [
      it.description,
      String(it.qty),
      money(it.unitNet),
      money(it.lineNet),
    ]),
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 9,
      lineColor: [0, 0, 0],
      lineWidth: 0.3,
      cellPadding: 2.5,
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      lineWidth: 0.3,
      lineColor: [0, 0, 0],
    },
    columnStyles: {
      0: { cellWidth: contentW - 25 - 25 - 30 },
      1: { cellWidth: 25, halign: 'center' },
      2: { cellWidth: 25, halign: 'right' },
      3: { cellWidth: 30, halign: 'right' },
    },
    margin: { left: marginX, right: marginX },
  })

  const afterTableY = doc.lastAutoTable.finalY

  // ---- Totals (bottom-right) ----
  const subtotalNet = items.reduce((s, it) => s + it.lineNet, 0)
  const vatAmount = subtotalNet * VAT_RATE
  const totalDue = subtotalNet + vatAmount

  const totalsLabelW = 40
  const totalsValueW = 30
  const totalsW = totalsLabelW + totalsValueW
  const totalsX = pageW - marginX - totalsW
  const totalsY = afterTableY + 6
  const rowH = 7

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  // Subtotal
  doc.rect(totalsX, totalsY, totalsLabelW, rowH)
  doc.rect(totalsX + totalsLabelW, totalsY, totalsValueW, rowH)
  doc.text('Subtotal', totalsX + 2, totalsY + 4.7)
  doc.text(money(subtotalNet), totalsX + totalsW - 2, totalsY + 4.7, { align: 'right' })
  // VAT
  doc.rect(totalsX, totalsY + rowH, totalsLabelW, rowH)
  doc.rect(totalsX + totalsLabelW, totalsY + rowH, totalsValueW, rowH)
  doc.text(`VAT (${(VAT_RATE * 100).toFixed(0)}%)`, totalsX + 2, totalsY + rowH + 4.7)
  doc.text(money(vatAmount), totalsX + totalsW - 2, totalsY + rowH + 4.7, { align: 'right' })
  // TOTAL DUE
  doc.setFont('helvetica', 'bold')
  doc.rect(totalsX, totalsY + 2 * rowH, totalsLabelW, rowH)
  doc.rect(totalsX + totalsLabelW, totalsY + 2 * rowH, totalsValueW, rowH)
  doc.text('TOTAL DUE', totalsX + 2, totalsY + 2 * rowH + 4.7)
  doc.text(`£${money(totalDue)}`, totalsX + totalsW - 2, totalsY + 2 * rowH + 4.7, { align: 'right' })

  // ---- Bottom-left: Payment + company regulatory info ----
  const infoX = marginX
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.text('Payment Terms:', infoX, totalsY + 2)
  doc.text('Prepaid', infoX + 32, totalsY + 2)
  doc.text('Payment Method:', infoX, totalsY + 8)
  doc.text('Prepaid', infoX + 32, totalsY + 8)

  // Registered Office / Company Reg / VAT anchored near bottom
  const footY = pageH - 25
  doc.setFontSize(9)
  doc.text('Registered Office Address:', infoX, footY)
  doc.setFont('helvetica', 'bold')
  doc.text(SELLER.registeredOfficeAddress.join(', '), infoX + 50, footY)
  doc.setFont('helvetica', 'normal')
  doc.text('Company Registration Number:', infoX, footY + 5)
  doc.setFont('helvetica', 'bold')
  doc.text(SELLER.companyNumber, infoX + 50, footY + 5)
  doc.setFont('helvetica', 'normal')
  doc.text('VAT Number:', infoX, footY + 10)
  doc.setFont('helvetica', 'bold')
  doc.text(SELLER.vatNumber, infoX + 50, footY + 10)

  doc.save(`VAT-invoice-${orderNum || 'order'}.pdf`)
}
